const MAX_RETRIES = 3;
// 408 e 429 são transitórios; o resto da faixa 4xx não muda com nova tentativa.
const RETRYABLE_CLIENT_ERRORS = new Set([408, 429]);

type ErrorShape = {
  response?: { status?: unknown };
  status?: unknown;
  // GraphQLRequester rejeita respostas sem `data` como `{ errors }`, com o status HTTP do resolver em cada item.
  errors?: { status?: unknown }[];
  // ApolloError de rede.
  networkError?: { statusCode?: unknown };
  // ApiError do @cobliteam/api, que na origem 'session' traz o motivo da falha no `code`.
  origin?: unknown;
  code?: unknown;
};

const getHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const { response, status, errors, networkError } = error as ErrorShape;
  const value = response?.status ?? status ?? networkError?.statusCode ?? errors?.find((item) => typeof item?.status === 'number')?.status;
  return typeof value === 'number' ? value : undefined;
};

// Sem access token para usar, nem guardado nem vindo da renovação, tentar de novo não muda o resultado.
const isWithoutSession = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const { origin, code } = error as ErrorShape;
  return origin === 'session' && code === 'no-session';
};

// Resposta fora do contrato volta igual na próxima tentativa.
const isInvalidResponse = (error: unknown) => !!error && typeof error === 'object' && (error as ErrorShape).code === 'invalid-response';

/** `retry` padrão das queries: até 3 tentativas, nenhuma sem sessão, com resposta fora do contrato ou em erro 4xx (endpoint inexistente, sem permissão etc.). */
export const shouldRetryQuery = (failureCount: number, error: unknown) => {
  if (isWithoutSession(error) || isInvalidResponse(error)) return false;
  const status = getHttpStatus(error);
  if (status !== undefined && status >= 400 && status < 500 && !RETRYABLE_CLIENT_ERRORS.has(status)) return false;
  return failureCount < MAX_RETRIES;
};
